import apiService from '../apiService';
import { getFinalPrice } from './priceHelpers';

// ============================================
// CART HELPERS WITH API INTEGRATION
// ============================================

/**
 * Add product to cart (with API sync)
 * @param {Array} cart - Current cart state
 * @param {Object} product - Product to add
 * @param {boolean} syncWithBackend - Whether to sync with backend API
 * @returns {Promise<Array>} Updated cart
 */
export const addToCart = async (cart, product, quantity = 1, syncWithBackend = true) => {
  const existing = cart.find(item => item.id === product.id);
  let updatedCart;
  if (existing) {
    updatedCart = cart.map(item =>
      item.id === product.id ? { ...item, quantity: item.quantity + quantity } : item
    );
  } else {
    updatedCart = [...cart, { ...product, quantity }];
  }

  // Sync with backend if authenticated
  if (syncWithBackend && apiService.isAuthenticated()) {
    try {
      // Final price = sale_price when set, else regular price (matches backend)
      const effectivePrice = getFinalPrice(product);

      const response = await apiService.addToCart({
        id: product.id,
        quantity,
        price: effectivePrice,
        name: product.name
      });
      
      if (response.success) {
        console.log('Cart synced with backend');
        // Optionally update cart with backend response
        if (response.data.cart) {
          return response.data.cart;
        }
      }
    } catch (error) {
      console.error('Failed to sync cart with backend:', error);
      // Continue with local update even if backend fails
    }
  }

  // Update localStorage for offline support
  localStorage.setItem('cart', JSON.stringify(updatedCart));
  
  return updatedCart;
};

/**
 * Update item quantity in cart (with API sync)
 * @param {Array} cart - Current cart state
 * @param {number} id - Product ID
 * @param {number} delta - Change in quantity (+1 or -1)
 * @param {boolean} syncWithBackend - Whether to sync with backend API
 * @returns {Promise<Array>} Updated cart
 */
export const updateQuantity = async (cart, id, delta, syncWithBackend = true) => {
  //alert('cart before updateQuantity:'+JSON.stringify(cart));
  const updatedCart = cart.map(item =>
    item.id === id ? { ...item, quantity: Math.max(0, item.quantity + delta) } : item
  ).filter(item => item.quantity > 0);

  // Sync with backend if authenticated
 // alert(`cartHelpers.updateQuantity called: id=${id}, delta=${delta}`);
 // console.log('cartHelpers.updateQuantity called:', { cart, id, delta, syncWithBackend });
  if (syncWithBackend && apiService.isAuthenticated()) {
    try {
      const item = cart.find(item => item.id === id);
      const newQuantity = item.quantity + delta;

      if (newQuantity > 0) {
        // Update quantity - prefer cart_item_id, fallback to product_id
        const cartItemId = item.cart_item_id;
        // alert(`Calling backend updateCartItem: cartItemId=${cartItemId || id}, newQuantity=${newQuantity}`);
        // console.log('Updating cart item:', { item, cartItemId, newQuantity });
        
        if (!cartItemId) {
          console.warn('cart_item_id missing, using product_id as fallback:', id);
        }
        
        const response = await apiService.updateCartItem(cartItemId || id, newQuantity);
        
        if (response.success && response.data.cart) {
          localStorage.setItem('cart', JSON.stringify(response.data.cart));
          return response.data.cart;
        }
      } else {
        // Remove item if quantity becomes 0
        const cartItemId = item.cart_item_id;
        // console.log('Removing cart item:', { item, cartItemId });
        // alert(`Calling backend removeFromCart: cartItemId=${cartItemId || id}`);
        
        if (!cartItemId) {
          console.warn('cart_item_id missing, using product_id as fallback:', id);
        }
        
        const response = await apiService.removeFromCart(cartItemId || id);
        
        if (response.success && response.data.cart) {
          localStorage.setItem('cart', JSON.stringify(response.data.cart));
          return response.data.cart;
        }
      }
    } catch (error) {
      console.error('Failed to sync quantity with backend:', error);
    }
  }

  // Update localStorage
  localStorage.setItem('cart', JSON.stringify(updatedCart));
  
  return updatedCart;
};

/**
 * Set exact quantity for cart item (with API sync)
 * @param {Array} cart - Current cart state
 * @param {number} id - Product ID
 * @param {number} quantity - Exact quantity to set
 * @param {boolean} syncWithBackend - Whether to sync with backend API
 * @returns {Promise<Array>} Updated cart
 */
export const setQuantity = async (cart, id, quantity, syncWithBackend = true) => {
  const updatedCart = cart.map(item =>
    item.id === id ? { ...item, quantity: Math.max(0, quantity) } : item
  ).filter(item => item.quantity > 0);

  // Sync with backend if authenticated
  if (syncWithBackend && apiService.isAuthenticated()) {
    try {
      const item = cart.find(item => item.id === id);
      const cartItemId = item?.cart_item_id || item?.cart_id || id;
      
      if (quantity > 0) {
        const response = await apiService.updateCartItem(cartItemId, quantity);
        
        if (response.success && response.data.cart) {
          localStorage.setItem('cart', JSON.stringify(response.data.cart));
          return response.data.cart;
        }
      } else {
        const response = await apiService.removeFromCart(cartItemId);
        
        if (response.success && response.data.cart) {
          localStorage.setItem('cart', JSON.stringify(response.data.cart));
          return response.data.cart;
        }
      }
    } catch (error) {
      console.error('Failed to set quantity with backend:', error);
    }
  }

  // Update localStorage
  localStorage.setItem('cart', JSON.stringify(updatedCart));
  
  return updatedCart;
};

/**
 * Remove product from cart (with API sync)
 * @param {Array} cart - Current cart state
 * @param {number} id - Product ID to remove
 * @param {boolean} syncWithBackend - Whether to sync with backend API
 * @returns {Promise<Array>} Updated cart
 */
export const removeFromCart = async (cart, id, syncWithBackend = true) => {
  const updatedCart = cart.filter(item => item.id !== id);

  // Sync with backend if authenticated
  if (syncWithBackend && apiService.isAuthenticated()) {
    try {
      const item = cart.find(item => item.id === id);
      const cartItemId = item?.cart_item_id || item?.cart_id || id;
      
      const response = await apiService.removeFromCart(cartItemId);
      
      if (response.success && response.data.cart) {
        localStorage.setItem('cart', JSON.stringify(response.data.cart));
        return response.data.cart;
      }
    } catch (error) {
      console.error('Failed to remove from cart in backend:', error);
    }
  }

  // Update localStorage
  localStorage.setItem('cart', JSON.stringify(updatedCart));
  
  return updatedCart;
};

/**
 * Clear entire cart (with API sync)
 * @param {boolean} syncWithBackend - Whether to sync with backend API
 * @returns {Promise<Array>} Empty cart
 */
export const clearCart = async (syncWithBackend = true) => {
  // Sync with backend if authenticated
  if (syncWithBackend && apiService.isAuthenticated()) {
    try {
      const response = await apiService.clearCart();
      
      if (response.success) {
        console.log('Cart cleared in backend');
      }
    } catch (error) {
      console.error('Failed to clear cart in backend:', error);
    }
  }

  // Clear localStorage
  localStorage.removeItem('cart');
  
  return [];
};

/**
 * Get cart total price
 * @param {Array} cart - Current cart state
 * @returns {number} Total price
 */
export const getCartTotal = (cart) => {
  if (!Array.isArray(cart)) return 0;
  return cart.reduce((sum, item) => {
    // Final price = sale_price when set, else regular price (matches backend)
    return sum + (getFinalPrice(item) * item.quantity);
  }, 0);
};

/**
 * Get total item count in cart
 * @param {Array} cart - Current cart state
 * @returns {number} Total item count
 */
export const getCartCount = (cart) => {
  if (!Array.isArray(cart)) return 0;
  return cart.reduce((sum, item) => sum + item.quantity, 0);
};

/**
 * Load cart from backend or localStorage
 * @returns {Promise<Array>} Cart items
 */
export const loadCart = async () => {
  // Try to load from backend if authenticated
  if (apiService.isAuthenticated()) {
    try {
      const response = await apiService.getCart();
      console.log("🛒 Load Cart Response:", JSON.stringify(response, null, 2));
      
      if (response.success && response.data) {
        // Handle different possible response structures
        let cartItems = [];
       if (response.data.cart && Array.isArray(response.data.cart.items)) {
          cartItems = response.data.cart.items;
        } else if (response.data.items && Array.isArray(response.data.items)) {
          cartItems = response.data.items;
        } else if (Array.isArray(response.data)) {
          cartItems = response.data;
        }
      
        console.log("🛒 Extracted cart items:", cartItems);
        // Map backend cart items to frontend format
        const mappedCart = cartItems.map(item => {
          // Extract product data from various possible locations
          const product = item.product || {};
          
          const cartItem = {
            id: item.product_id || product.product_id || product.id || item.id,
            cart_item_id: item.cart_item_id || item.id, // Backend cart record ID
            name: item.product_name || item.name || product.name || '',
            price: parseFloat(item.price || product.price || 0),
            sale_price: item.sale_price || product.sale_price || null,
            quantity: parseInt(item.quantity || 1),
            category: item.category || product.category || '',
            icon: item.icon || product.icon || '🥛',
            // Try multiple possible image field locations
            image: item.image || 
                   item.product_image || 
                   product.image || 
                   product.product_image ||
                   product.featured_image ||
                   product.thumbnail ||
                   product.image_url ||
                   null,
            featured_image: product.featured_image || 
                           item.featured_image ||
                           item.image || 
                           product.image ||
                           null,
            unit: item.unit || product.unit || '1L',
            // Include additional fields that might be useful
            description: item.description || product.description || '',
            fat: item.fat || product.fat || null,
            subscription: item.subscription || product.subscription || null
          };
          console.log('🛒 Mapped cart item:', { 
            original: item, 
            product: product,
            mapped: cartItem,
            imageFields: {
              'item.image': item.image,
              'item.product_image': item.product_image,
              'product.image': product.image,
              'product.featured_image': product.featured_image,
              'final': cartItem.image
            }
          });
          return cartItem;
        });
        
        // Sync with localStorage
        localStorage.setItem('cart', JSON.stringify(mappedCart));
         return mappedCart;
      } else {
        console.log("No cart data from backend, loading from localStorage.");
      }
    } catch (error) {
      console.error('Failed to load cart from backend:', error);
    }
  } else {
    console.log("User not authenticated. Loading cart from localStorage.");
  }

  // Fallback to localStorage
  try {
    const localCart = localStorage.getItem('cart');
    const parsedCart = localCart ? JSON.parse(localCart) : [];
    return Array.isArray(parsedCart) ? parsedCart : [];
  } catch (error) {
    console.error('Failed to parse cart from localStorage:', error);
    return [];
  }
};

/**
 * Sync local cart with backend (useful after login)
 * @param {Array} cart - Local cart to sync
 * @returns {Promise<Array>} Synced cart from backend
 */
export const syncCartWithBackend = async (cart) => {
  if (!apiService.isAuthenticated()) {
    console.warn('Cannot sync cart: User not authenticated');
    return cart;
  }

  try {
    const response = await apiService.syncCart(cart);
  //  alert("Sync Cart Response:"+JSON.stringify(response));
    if (response.success && response.data.cart) {
      const syncedCart = response.data.cart;
      // Update localStorage with synced cart
      localStorage.setItem('cart', JSON.stringify(syncedCart));
      console.log('Cart synced successfully with backend');
      return syncedCart;
    }
  } catch (error) {
    console.error('Failed to sync cart with backend:', error);
  }

  return cart;
};

/**
 * Validate cart items (check stock availability, prices)
 * @param {Array} cart - Current cart state
 * @returns {Promise<Object>} Validation result
 */
export const validateCart = async (cart) => {
  if (!apiService.isAuthenticated()) {
    return { valid: true, cart, errors: [] };
  }

  try {
    // You can add a dedicated validation endpoint
    // For now, we'll just fetch the latest cart
    const response = await apiService.getCart();
    
    if (response.success && response.data.cart) {
      return {
        valid: true,
        cart: response.data.cart,
        errors: []
      };
    }
  } catch (error) {
    console.error('Cart validation failed:', error);
    return {
      valid: false,
      cart,
      errors: ['Failed to validate cart']
    };
  }

  return { valid: true, cart, errors: [] };
};

/**
 * Apply coupon to cart
 * @param {string} couponCode - Coupon code to apply
 * @returns {Promise<Object>} Coupon application result
 */
export const applyCoupon = async (couponCode) => {
  if (!apiService.isAuthenticated()) {
    return { success: false, message: 'Please login to apply coupon' };
  }

  try {
    // You'll need to add this endpoint to apiService
    const response = await apiService.post('/cart/apply-coupon', {
      coupon_code: couponCode
    }, { auth: true });
    
    return response;
  } catch (error) {
    console.error('Failed to apply coupon:', error);
    return { success: false, message: 'Failed to apply coupon' };
  }
};

/**
 * Get cart with calculated totals and discounts
 * @param {Array} cart - Current cart state
 * @returns {Object} Cart summary with totals
 */
export const getCartSummary = (cart) => {
  if (!Array.isArray(cart)) {
    return {
      items: [],
      itemCount: 0,
      subtotal: 0,
      deliveryFee: 0,
      discount: 0,
      total: 0
    };
  }
  
  const subtotal = getCartTotal(cart);
  const itemCount = getCartCount(cart);
  const deliveryFee = subtotal > 500 ? 0 : 40; // Free delivery above ₹500
  const discount = 0; // Will be calculated if coupon applied
  const total = subtotal + deliveryFee - discount;

  return {
    items: cart,
    itemCount,
    subtotal,
    deliveryFee,
    discount,
    total
  };
};